import classNames from "classnames";
import { Fragment, useEffect } from "react";
import { useLocalStorage } from "react-use";

import { Specimen } from "pwapi";

import { CaseDiagnosis } from "custom-models";

import { showMask } from "shared/actions/slideDetailActions";
import { setInputMacroCode } from "shared/actions/specimenDetailActions";
import { LesionLine, lesionLineTypeByName } from "shared/reducers/aiReducer";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { macroCodesWithSubtypePossibilities } from "shared/utils/aiLinesAndSubtypes";
import { caseSlideLabel } from "shared/utils/slideUtils";

import { PositiveLesionLine, usePositiveLesionLines } from "hooks/aiHooks";
import { useAppDispatch, useAppSelector } from "store/hooks";

export const alternateMacroCodes = {
  WDSCC: ["SCCKA", "SUPSCC"],
  SCCIS: ["AK", "SCCISAE"],
  AK: ["SCCIS", "HAK"],
  BCCS: ["BCCSN", "BCCN"],
  BCCN: ["BCCSN", "BCCNI"],
  BCCI: ["BCCN", "BCCNI"],
  BCCSN: ["BCCN", "BCCS"],
  BCCNI: ["BCCN", "BCCI"],
  BCCSNI: ["BCCNI", "BCCSI"],
  SKII: ["SKVV", "SKP"],
  VVII: ["VVIR", "SKVV"],
  PC: ["PCR", "EIC"],
  EIC: ["EICR", "MIL"],
};

export const mergeAlternateMacroCodes = (aicodes: string[] | undefined) => {
  if (!aicodes) return [];
  const aicodesUC = [...aicodes.map((v) => v.toUpperCase())];

  const merged = aicodesUC.reduce((acc: string[], code) => {
    const alternateCodes =
      alternateMacroCodes[code as keyof typeof alternateMacroCodes];
    if (alternateCodes) {
      acc.push(...alternateCodes);
    }
    return acc;
  }, []);

  return [...new Set(merged)].filter((v) => !aicodesUC.includes(v));
};

type DiagnosisDialogAIProps = {
  currentSpecimen?: Specimen;
  currentDiagnosis: CaseDiagnosis;
  prospectiveMacroCode: string;
  updateDiagnosis: Function;
  positiveLesionLines?: LesionLine[];
  lesionLine?: LesionLine;
  setProspectiveMacroCode?: React.Dispatch<React.SetStateAction<string>>;
  macroCodeInputRef?: React.RefObject<HTMLInputElement>;
};
const DiagnosisDialogAI = ({
  positiveLesionLines,
  currentSpecimen,
  prospectiveMacroCode,
  setProspectiveMacroCode,
  macroCodeInputRef,
}: DiagnosisDialogAIProps) => {
  const dispatch = useAppDispatch();
  const caseDetail = useAppSelector(({ caseDetail }) => caseDetail);
  const autoFillAIMacroCode = useAppSelector((state) =>
    selectFeatureFlag(state, "autoFillAIMacroCode")
  );

  const inputMacroCode = useAppSelector(
    (state) =>
      state.caseDetail.specimenDetail.find((s) => s.id === currentSpecimen?.id)
        ?.inputMacroCode
  );

  const positiveLines = usePositiveLesionLines(
    positiveLesionLines,
    currentSpecimen
  );

  const allDxcodes = [
    ...new Set(
      positiveLines.flatMap((p) =>
        p.linesOfName.flatMap((l) => macroCodesWithSubtypePossibilities(l))
      )
    ),
  ].filter((v) => v);

  const handleClickDxCode = (
    positiveLine: PositiveLesionLine,
    dxcode: string
  ) => {
    if (currentSpecimen?.id)
      dispatch(setInputMacroCode(currentSpecimen?.id, dxcode));
    dispatch(
      showMask(lesionLineTypeByName(positiveLine?.diagnosis_name || ""))
    );
    macroCodeInputRef?.current?.focus();
  };

  // Ported from DiagnosisDialog.tsx, probably better belongs here
  // Sets the input if only 1 macro code exists
  useEffect(() => {
    if (
      setProspectiveMacroCode &&
      allDxcodes.length === 1 &&
      currentSpecimen?.id &&
      !prospectiveMacroCode &&
      typeof inputMacroCode === "undefined" &&
      !autoFillAIMacroCode
    ) {
      setProspectiveMacroCode(allDxcodes.at(0) || "");
    }
  }, [
    allDxcodes,
    currentSpecimen?.id,
    prospectiveMacroCode,
    inputMacroCode,
    autoFillAIMacroCode,
  ]);

  // Hack to fix widths on the subtypes
  const [commonWidth] = useLocalStorage<number | undefined>(
    "DIAGNOSIS_TEXTAREA_WIDTH"
  );
  // If it's under 400, bump it to 400
  const subtypeMaxWidth = commonWidth
    ? commonWidth < 400
      ? 400
      : commonWidth
    : undefined;

  return (
    <div>
      {(!positiveLines || positiveLines?.length < 1) && (
        <div className="Sidebar__AI">
          <p>
            <strong>No Positive Lesion Lines</strong>
          </p>
        </div>
      )}

      {positiveLines.map((p) => {
        const dxcodes: string[] = [
          ...new Set(
            p.linesOfName.map((line) =>
              macroCodesWithSubtypePossibilities(line)
            ) as string[]
          ),
        ].filter((v) => v);
        const slides = caseDetail.slides?.filter((s) =>
          p.slide_ids.includes(s.id)
        );
        const margins = [
          ...new Set(p.linesOfName.map((l) => l.margin_status)),
        ].filter((v) => v);
        const alternateDxcodes = mergeAlternateMacroCodes(dxcodes as string[]);

        return (
          <div
            className="Sidebar__AI"
            key={`${p.diagnosis_name}-${p.slide_ids?.join(",")}`}
          >
            <div>
              <div className="Sidebar__AI__diagnosis">
                <p>
                  <span className="Sidebar__AI__name">
                    {p.diagnosis_name}
                    {!!slides?.length && (
                      <span>
                        {" "}
                        &mdash;{slides.length > 2 ? <br /> : ""}
                        <span
                          className={classNames({
                            "Sidebar__AI__slides--long": slides.length > 2,
                          })}
                          style={{ maxWidth: subtypeMaxWidth }}
                        >
                          {slides
                            .map((s) => caseSlideLabel(s, caseDetail))
                            .join(", ")}
                        </span>
                      </span>
                    )}
                  </span>
                  <span
                    className="LesionLineType__subtypes"
                    style={{ maxWidth: subtypeMaxWidth }}
                  >
                    <span className="LesionLineType__legend">
                      {p.subtypes.join(", ")}
                      {/* TODO: separate conclusive, inconclusive */}
                    </span>
                    {!!margins.length && (
                      <>
                        {" "}
                        &mdash;{" "}
                        <code>
                          {margins.join(", ")} Margin
                          {margins.length > 1 && <>s</>}
                        </code>
                      </>
                    )}
                  </span>
                  {/* <SubtypesAndMargin lesionLine={diagnosableLesionLine} /> */}
                  {/* positive margins [only], positive & negative margins, negative margins [only] */}
                </p>
              </div>
              {dxcodes.map((dxcode) => (
                <button
                  key={dxcode}
                  className={classNames("Sidebar__AI__macro", {
                    "Sidebar__AI__macro--selected": dxcode === inputMacroCode,
                  })}
                  title={dxcode}
                  onClick={() => {
                    handleClickDxCode(p, dxcode);
                  }}
                  onFocus={
                    setProspectiveMacroCode
                      ? () => setProspectiveMacroCode(dxcode)
                      : undefined
                  }
                  onBlur={
                    setProspectiveMacroCode
                      ? () => setProspectiveMacroCode("")
                      : undefined
                  }
                >
                  <img src="/pw-white.png" alt="" />
                  <span>{dxcode}</span>
                </button>
              ))}
              {alternateDxcodes?.map((altCode) => (
                <button
                  key={altCode}
                  className={classNames(
                    "Sidebar__AI__macro Sidebar__AI__macro--alternate",
                    {
                      "Sidebar__AI__macro--selected":
                        altCode === inputMacroCode,
                    }
                  )}
                  title={`${altCode} (alternate)`}
                  onClick={() => {
                    handleClickDxCode(p, altCode);
                  }}
                  onFocus={
                    setProspectiveMacroCode
                      ? () => setProspectiveMacroCode(altCode)
                      : undefined
                  }
                  onBlur={
                    setProspectiveMacroCode
                      ? () => setProspectiveMacroCode("")
                      : undefined
                  }
                >
                  {altCode}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DiagnosisDialogAI;
